import { useEffect, useState } from 'react'
import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { api } from '@/api/client'

/**
 * Keeps the Clerk auth token fresh on the axios instance.
 * Blocks rendering of children until the first token is set,
 * preventing 401 errors from providers that make API calls on mount.
 */
export function AuthTokenProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn, isLoaded } = useClerkAuth()
  const [tokenReady, setTokenReady] = useState(false)

  useEffect(() => {
    if (!isLoaded) return

    if (!isSignedIn) {
      // Not signed in — clear token and render children (public pages)
      delete api.defaults.headers.common['Authorization']
      setTokenReady(true)
      return
    }

    const setToken = async () => {
      try {
        const token = await getToken()
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }
      } catch {
        // Token fetch failed — will retry on next interval
      }
      setTokenReady(true)
    }

    setToken()

    const interval = setInterval(setToken, 50 * 1000) // Clerk tokens expire in 60s
    return () => clearInterval(interval)
  }, [isSignedIn, isLoaded, getToken])

  // Don't render children until auth state is resolved
  if (!tokenReady) return null

  return <>{children}</>
}
